import { useEffect, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@/lib/use-server-fn";
import { getMe } from "@/lib/admin.functions";

export function useAdminAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const callGetMe = useServerFn(getMe);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const checkAdmin = () => {
      setTimeout(async () => {
        try {
          const me = await callGetMe();
          if (mounted.current) setIsAdmin(!!me.isAdmin);
        } catch {
          if (mounted.current) setIsAdmin(false);
        } finally {
          if (mounted.current) setLoading(false);
        }
      }, 0);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession) {
        checkAdmin();
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (data.session) {
        checkAdmin();
      } else {
        setLoading(false);
      }
    });

    return () => {
      mounted.current = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, user, isAdmin, loading };
}
